import { getSecret } from "./get-secret.js";
import sendgrid from "@sendgrid/mail";
import { SQSClient, SendMessageCommand } from "@aws-sdk/client-sqs";
import { getDb } from "../db.js";
import { getAwardSettings } from "../awards/get-award-settings.js";
import { isRejected } from "./types.js";
const sqsClient = new SQSClient({ region: "us-west-2" });
let SENDGRID_API_KEY_FULL_ACCESS = "";
export async function sendEmail(sendEmailParams) {
    if (!SENDGRID_API_KEY_FULL_ACCESS) {
        ({ SENDGRID_API_KEY_FULL_ACCESS } = await getSecret("SENDGRID_API_KEY_FULL_ACCESS"));
    }
    sendgrid.setApiKey(SENDGRID_API_KEY_FULL_ACCESS);
    const params = {
        to: sendEmailParams.to,
        from: sendEmailParams.from,
        subject: sendEmailParams.subject,
        text: sendEmailParams.text,
        html: sendEmailParams.html,
        dynamicTemplateData: sendEmailParams.dynamicTemplateData ?? {},
    };
    const { sendAt, bcc, templateId, table } = sendEmailParams;
    if (sendAt) {
        params.sent_at = sendAt;
    }
    if (bcc?.length) {
        params.bcc = bcc;
    }
    if (templateId) {
        params.templateId = templateId;
    }
    // unsubscribe link
    let appUrl = params.dynamicTemplateData.awardSettings?.appUrl;
    if (templateId && !appUrl && table) {
        try {
            const awardSettings = await getAwardSettings(table);
            appUrl = awardSettings?.appUrl;
        }
        catch (error) {
            console.error(error);
        }
    }
    if (templateId && appUrl) {
        let base64email = "";
        if (typeof params.to === "string") {
            base64email = Buffer.from(params.to, "utf8").toString("base64");
        }
        else if (params.to.length === 1) {
            base64email = Buffer.from(params.to[0], "utf8").toString("base64");
        }
        else if (params.to.email) {
            base64email = Buffer.from(params.to.email, "utf8").toString("base64");
        }
        if (base64email) {
            params.dynamicTemplateData.unsubscribeLink = `${appUrl}/dashboard/unsubscribe/${base64email}`;
        }
    }
    if (process.env.ENV !== "production") {
        params.to = "boadevtest@gmail.com";
        params.bcc = undefined;
        if (params.dynamicTemplateData.unsubscribeLink) {
            params.dynamicTemplateData.unsubscribeLink = `${appUrl}/dashboard/unsubscribe/${Buffer.from(params.to, "utf8").toString("base64")}`;
        }
    }
    const [response] = await sendgrid.send(params);
    const toEmail = typeof sendEmailParams.to === "string"
        ? sendEmailParams.to
        : sendEmailParams.to?.email ||
            sendEmailParams.to?.[0]?.email ||
            sendEmailParams.to?.[0];
    if (typeof toEmail === "string") {
        const { putItem } = getDb();
        const timestamp = new Date().toISOString();
        const { headers: _headers, ...rest } = response;
        const promises = [
            putItem({
                TableName: "sendgrid-events",
                Item: {
                    PK: toEmail,
                    SK: response.headers["x-message-id"],
                    GSI1_PK: sendEmailParams.table || "unknown",
                    GSI1_SK: `${toEmail}#${timestamp}`,
                    GSI2_PK: sendEmailParams.table || "unknown",
                    GSI2_SK: timestamp,
                    ...rest,
                    params,
                },
            }),
        ];
        if (sendEmailParams.table) {
            promises.push(createActivityItem({
                table: sendEmailParams.table,
                toEmail,
                timestamp,
                messageId: "",
                subject: params.subject || "",
                content: params.html || params.text || "",
            }));
        }
        const responses = await Promise.allSettled(promises);
        const rejected = responses.filter(isRejected);
        if (rejected.length) {
            console.error(`Failed to record email activity for ${toEmail}`);
            console.error(rejected);
            const errors = rejected.map((r) => r.reason).join("\n");
            await sendErrorEmail(new Error(`Failed to record email activity ${toEmail} ${errors}`));
        }
    }
    return response;
}
export async function sendErrorEmail(error) {
    const { message, stack } = error;
    const params = {
        to: { email: "davidhenrycoleman@gmail.com" },
        from: { email: "no-reply@inboundsystems.com", name: "Inbound Systems" },
        subject: "Error in best-of-awards-api",
        text: `${message}\n\n${stack}`,
    };
    return queueEmail(params);
}
export async function queueEmail(params) {
    const { to, bcc = [], from, subject, text, html, sendAt = "", templateId, dynamicTemplateData = {}, table = "unknown", } = params;
    return sqsClient.send(new SendMessageCommand({
        QueueUrl: process.env.EMAIL_QUEUE_URL,
        MessageBody: JSON.stringify({
            to,
            bcc,
            from,
            subject,
            text,
            html,
            sendAt,
            templateId,
            dynamicTemplateData,
            table,
        }),
    }));
}
async function createActivityItem({ table, toEmail, timestamp, messageId, subject, content, }) {
    const db = getDb();
    let GSI1_PK = undefined;
    let GSI1_SK = undefined;
    let GSI2_PK = undefined;
    let GSI2_SK = undefined;
    const contactItems = await db.query({
        TableName: table,
        IndexName: "BY_EMAIL",
        KeyConditionExpression: "emailAddress = :toEmail",
        FilterExpression: "begins_with(PK, :CONTACT) and (SK = :METADATA or begins_with(SK, :CANDIDATE))",
        ExpressionAttributeValues: {
            ":toEmail": toEmail.toLocaleLowerCase(),
            ":CONTACT": "CONTACT#",
            ":METADATA": "METADATA",
            ":CANDIDATE": "CANDIDATE#",
        },
    });
    const contactCandidate = contactItems.find((c) => c.SK.startsWith("CANDIDATE#"));
    const contactMetadata = contactItems.find((c) => c.SK === "METADATA");
    if (contactCandidate) {
        GSI1_PK = contactCandidate.GSI1_PK;
        GSI1_SK = `ACTIVITY#${timestamp}`;
        GSI2_PK = contactCandidate.GSI2_PK;
        GSI2_SK = `ACTIVITY#${timestamp}`;
    }
    if (contactMetadata) {
        GSI2_PK = contactMetadata.GSI2_PK;
        GSI2_SK = `ACTIVITY#${timestamp}`;
    }
    const item = {
        PK: `SENT-EMAIL#${toEmail}`,
        SK: timestamp,
        GSI1_PK,
        GSI1_SK,
        GSI2_PK,
        GSI2_SK,
        activityName: "Sent Email",
        activityType: "Sent Email",
        emailAddress: toEmail,
        subject,
        messageId,
        content,
        timestamp,
        type: "Activity",
        user: {
            candidateId: contactCandidate?.GSI1_PK
                ? contactCandidate.GSI1_PK.split("#")[1]
                : "",
            email: toEmail,
            firstName: contactMetadata?.firstName || "",
            lastName: contactMetadata?.lastName || "",
            phone: contactMetadata?.mobile || "",
            role: "Candidate",
            title: contactMetadata?.title || "",
        },
    };
    return await db.putItem({
        TableName: table,
        Item: item,
    });
}
//# sourceMappingURL=send-email.js.map